import { Link, useLocation } from "@tanstack/react-router";

import { useState } from "react";
import {
  ListVideoIcon,
  MenuIcon,
  Trash2Icon,
  UserCircle2Icon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";
import { Button } from "./ui/button";
import FooterLinks from "./FooterLinks";
import LanguageSelector from "./LanguageSelector";
import { useIsLoggedIn } from "@/stores/useAuth";
import { cn } from "@/lib/utils";
import { useAppStateStore } from "@/stores/useAppState";
import { appEnvironment, isProductionEnvironment } from "@/lib/appEnvironment";
import { resetAppData } from "@/lib/resetAppData";

const Header: React.FC<React.ComponentProps<"header">> = ({
  className,
  ...props
}) => {
  const { pathname } = useLocation();

  const isLoggedIn = useIsLoggedIn();

  const openLoginModal = useAppStateStore((s) => s.openLoginModal);
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <header
      className={cn("bg-neutral-70 text-white shadow-lg", className)}
      {...props}
    >
      <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-2 p-6">
        <h1 className="text-xl font-semibold">
          <Link to="/">
            <img
              src="/Logo.webp"
              alt={t("header_logo_alt")}
              className="h-10 w-auto"
              width="122"
              height="40"
            />
          </Link>
        </h1>
        <div className="flex items-center gap-2">
          {isLoggedIn ? (
            <>
              {pathname !== "/profile" && (
                <Link
                  to="/profile"
                  className="rounded-lg p-2 transition-colors hover:bg-gray-700"
                  aria-label={t("header_profile_open_aria")}
                >
                  <UserCircle2Icon size={24} />
                </Link>
              )}
            </>
          ) : (
            <Button
              className="min-w-24"
              size="sm"
              onClick={() => openLoginModal({ origin: "header" })}
            >
              {t("auth_signin_button")}
            </Button>
          )}

          <Sheet onOpenChange={setIsOpen} open={isOpen}>
            <SheetTrigger asChild>
              <button
                data-test-id="header-menu-trigger"
                className="rounded-lg p-2 transition-colors hover:bg-gray-700"
                aria-label={t("header_menu_trigger_aria")}
              >
                <MenuIcon size={24} />
              </button>
            </SheetTrigger>
            <SheetContent data-test-id="header-menu">
              <SheetHeader>
                <SheetTitle className="sr-only">
                  {t("header_menu_title")}
                </SheetTitle>
              </SheetHeader>
              <div className="overflow-y-auto px-6 pb-10">
                <Link
                  to="/my-list"
                  data-test-id="header-menu-my-list-link"
                  className="mb-6 flex items-center gap-2 text-white"
                  onClick={() => setIsOpen(false)}
                >
                  <ListVideoIcon size={20} />
                  {t("header_my_list_link")}
                </Link>
                <LanguageSelector onSelect={() => setIsOpen(false)} />
                <FooterLinks stacked onNavigate={() => setIsOpen(false)} />
                {!isProductionEnvironment && (
                  <Button
                    variant="tertiary"
                    size="sm"
                    className="mt-8 w-full"
                    data-test-id="header-menu-reset-app-data"
                    onClick={() => {
                      if (
                        window.confirm(
                          "Clear all app data and reload? You will be signed out.",
                        )
                      )
                        resetAppData();
                    }}
                  >
                    <Trash2Icon size={16} />
                    Reset app data ({appEnvironment})
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
